// HQ review of MightyExchange submissions. The sanitized document text is
// loaded, and the Word file can be downloaded, before approve or reject.
// Reject requires a note. Copy says dynamic document, never questionnaire.
import React, { useEffect, useState } from 'react';
import { adminApi } from '../utils/adminApi';

const font = { fontFamily: 'Arial, sans-serif' };
const NAVY = '#0F1838';

export default function AdminHQExchangePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewReady, setPreviewReady] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.hqExchangeList();
      const list = Array.isArray(data) ? data : (data.listings || data.queue || data.submissions || []);
      setRows(list);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function openRow(row) {
    setSelected(row);
    setPreview(null);
    setPreviewError('');
    setPreviewReady(false);
    setNote('');
    setNotice('');
    setError('');
    try {
      const data = await adminApi.hqExchangePreview(row.id);
      setPreview(data || {});
    } catch (e) {
      setPreviewError(e.message || 'The document preview could not be loaded.');
    } finally {
      setPreviewReady(true);
    }
  }

  async function download() {
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      const blob = await adminApi.hqExchangeDocument(selected.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (preview && preview.filename) || 'exchange-document.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function review(action) {
    if (!selected || !previewReady) return;
    if (action === 'reject' && !note.trim()) {
      setError('A note is required to reject a document.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await adminApi.hqExchangeReview(selected.id, action, note.trim());
      setNotice(action === 'approve' ? 'Approved.' : 'Rejected.');
      setSelected(null);
      setPreview(null);
      setNote('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const card = { background: '#fff', border: '1px solid #e2e2e2', borderRadius: 8, padding: 16, marginBottom: 12, ...font };

  return (
    <div style={font}>
      <h1 className="page-title" style={{ color: NAVY }}>MightyExchange review</h1>
      <p className="page-subtitle">
        Read the sanitized document before you approve or reject it. A rejection needs a written note.
      </p>
      {notice && <div style={{ ...card, background: '#f0faf5', borderColor: '#4caf7d' }}>{notice}</div>}
      {error && <div style={{ ...card, background: '#fdf0f0', borderColor: '#e08080' }}>{error}</div>}
      {loading ? <p>Loading…</p> : rows.length === 0 ? (
        <div style={card}>Nothing is waiting for review.</div>
      ) : rows.map((row) => (
        <div key={row.id} style={card}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong>{row.label || row.title || 'Document'}</strong>
            <span style={{ fontSize: 12, color: '#555' }}>{row.firm_name || ''}</span>
            <span style={{ fontSize: 12, color: '#777' }}>{row.status || ''}</span>
            <span style={{ fontSize: 12, color: '#777' }}>
              {row.has_document ? 'Document attached' : 'No document file'}
            </span>
            <button type="button" className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => openRow(row)}>
              {selected && selected.id === row.id ? 'Reviewing' : 'Review'}
            </button>
          </div>
        </div>
      ))}

      {selected && (
        <div style={{ ...card, borderColor: '#b9cbe8' }}>
          <h2 style={{ marginTop: 0, fontSize: 18 }}>{selected.label || 'Document'}</h2>
          <p style={{ fontSize: 13, color: '#555' }}>
            {selected.has_document
              ? 'This is the sanitized text. Download the Word file if you need the formatted copy.'
              : 'This submission has no Word document to preview.'}
          </p>
          {!previewReady && <p style={{ fontSize: 13 }}>Loading the document…</p>}
          {previewError && <p style={{ fontSize: 13, color: '#8a1f1f' }}>{previewError}</p>}
          {preview && preview.text && (
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'Georgia, serif', fontSize: 14, background: '#faf8f3', border: '1px solid #e3ddd0', borderRadius: 6, padding: 12, maxHeight: 360, overflow: 'auto' }}>
              {preview.text}
            </pre>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <button type="button" className="btn btn-sm" onClick={download} disabled={busy || !selected.has_document}>
              Download document
            </button>
          </div>
          <label style={{ display: 'block', fontSize: 13, marginTop: 12 }}>
            Note (required to reject)<br />
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} style={{ width: '100%', marginTop: 4, fontFamily: 'Arial, sans-serif' }} />
          </label>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="button" className="btn btn-sm btn-primary" disabled={busy || !previewReady} onClick={() => review('approve')}>
              Approve
            </button>
            <button type="button" className="btn btn-sm" disabled={busy || !previewReady || !note.trim()} onClick={() => review('reject')} style={{ borderColor: '#B85042', color: '#B85042' }}>
              Reject
            </button>
            <button type="button" className="btn btn-sm" onClick={() => { setSelected(null); setPreview(null); }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
